import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Lead, MonitoringConfig } from "@/types/video-call";
import audioLinesIcon from "@/assets/audio-lines.svg";

interface InterviewReadyScreenProps {
  lead: Lead;
  config: MonitoringConfig;
  onStartInterview: () => void;
  isStartingInterview: boolean;
  token?: string;
  interviewDetails?: {
    assistantName?: string;
    interviewType?: string;
    agent?: {
      name?: string;
    };
    job_title?: string;
    company_name?: string;
    lead_contact_name?: string;
    metadata?: {
      variables?: {
        job_title?: string;
        company_name?: string;
        lead_contact_name?: string;
        [key: string]: any;
      };
    };
  };
}


export const InterviewReadyScreen: React.FC<InterviewReadyScreenProps> = ({
  lead,
  config: _config,
  onStartInterview,
  isStartingInterview,
  token,
  interviewDetails,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [guidelinesConfirmed, setGuidelinesConfirmed] = useState(true);
  const [recordingConsent, setRecordingConsent] = useState(true);
  const [shareWithOtherOpenings, setShareWithOtherOpenings] = useState(true);

  // Initialize camera stream
  useEffect(() => {
    const initCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 }
          },
          audio: false // We don't need audio for preview
        });
        
        setCameraStream(stream);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (error) {
        console.error("Error accessing camera:", error);
        setCameraError("Unable to access camera");
      }
    };

    initCamera();
  }, []);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  // Update video element when stream changes
  useEffect(() => {
    if (videoRef.current && cameraStream) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [cameraStream]);


  return (
    <div className="min-h-screen bg-gradient-to-tr from-[#f2e9f8] to-white" style={{ fontFamily: 'Satoshi, sans-serif' }}>
      {/* Header */}
      <div className="w-full px-6 py-4 bg-white border-b border-gray-200">
        <div className="flex items-center gap-2">
          <img
            src="/hirevox.svg"
            alt="HireVox Logo"
            className="h-10"
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col items-center justify-center px-4 py-4 min-h-[calc(100vh-80px)]">
        {/* Welcome Message */}
        <div className="text-center mb-8 max-w-4xl">
          <h1 className="text-gray-800 text-[32px] font-semibold font-general-sans mb-2">
            Hi {(() => {
              const leadContactName = interviewDetails?.metadata?.variables?.lead_contact_name;
              const firstName = lead.first_name;
              const emailUser = lead.email?.split('@')[0];
              
              const finalName = leadContactName || firstName || emailUser || 'Guest';
              
              return finalName;
            })()}, Welcome to Your AI Video Interview
          </h1>
          <p className="text-gray-800 text-xl font-normal font-general-sans">
            The setup is complete and you are about to start a video interview
          </p>
        </div>

        {/* Main Content Container */}
        <div className="flex items-stretch justify-center gap-8 w-full max-w-6xl">
          {/* Camera Preview Card */}
          <div className="w-full max-w-[600px]">
            <div className="bg-white rounded-xl border border-neutral-100 h-full flex flex-col overflow-hidden">
              <div className="relative w-full h-full bg-gray-200 flex-1">
                {cameraError ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="text-red-600 mb-2">📷</div>
                      <p className="text-sm text-gray-600">{cameraError}</p>
                    </div>
                  </div>
                ) : (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover transform scale-x-[-1]"
                  />
                )}
                <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-md text-sm font-medium">
                  {(() => {
                    const leadContactName = interviewDetails?.metadata?.variables?.lead_contact_name;
                    const firstName = lead.first_name;
                    const emailUser = lead.email?.split('@')[0];
                    
                    const finalName = leadContactName || firstName || emailUser || 'Guest';
                    
                    return finalName;
                  })()}
                </div>
                {/* Chat bubble */}
                {/* <img
                  src={audioLinesIcon}
                  alt="Audio lines"
                  className="absolute bottom-4 left-4 w-8 h-8"
                /> */}
              </div>
            </div>
          </div>

          {/* Interview Details Card */}
          <div className="w-full max-w-[600px]">
            <div className="bg-white rounded-xl border border-neutral-100 p-5 h-full flex flex-col">
              <div className="space-y-4 flex-1 font-satoshi">
                <div className="flex gap-4 justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-normal text-gray-500">Role</p>
                    <p className="text-base font-bold text-gray-800 mt-0.5">
                      {interviewDetails?.metadata?.variables?.job_title || ""}
                    </p>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-normal text-gray-500">At</p>
                    <p className="text-base font-bold text-gray-800 mt-0.5">
                      {interviewDetails?.metadata?.variables?.company_name || ""}
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-200 my-4" />

                <div className="space-y-4 text-[#1F2937]">
                  <div className="space-y-1">
                    <h3 className="text-base font-medium">Before you start</h3>
                    <p className="text-sm">
                      This interview is monitored by AI and reviewed by human evaluators to ensure a fair evaluation.
                    </p>
                  </div>
                  <div className="space-y-3">
                    <h3 className="text-base font-medium">By continuing, you confirm that</h3>
                    <ul className="text-sm space-y-1.5 list-none pl-0">
                      <li className="flex gap-2">
                        <span className="flex-shrink-0 size-1.5 rounded-full bg-black mt-1.5" aria-hidden />
                        <span>You will not attempt to cheat during the interview</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="flex-shrink-0 size-1.5 rounded-full bg-black mt-1.5" aria-hidden />
                        <span>You will not use external help such as ChatGPT, Google, AI assistants, notes, or other people</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="flex-shrink-0 size-1.5 rounded-full bg-black mt-1.5" aria-hidden />
                        <span>All responses you provide will be your own work</span>
                      </li>
                    </ul>
                  </div>
                  <p className="text-sm font-medium">
                    If suspicious behavior is detected you will be disqualified, and you may be restricted from evaluations across 500+ companies using HireVox.
                  </p>
                </div>
              </div>

              <div className="mt-4 flex-shrink-0 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer font-satoshi bg-[#F7F7F7] p-2.5 rounded">
                  <input
                    type="checkbox"
                    checked={guidelinesConfirmed}
                    onChange={(e) => setGuidelinesConfirmed(e.target.checked)}
                    className="rounded border-gray-300 text-[#8437BB] focus:ring-[#8437BB] size-4 flex-shrink-0"
                  />
                  <span className="text-xs font-bold text-[#616161]">I understand the guidelines and confirm.</span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer font-satoshi bg-[#F7F7F7] p-2.5 rounded">
                  <input
                    type="checkbox"
                    checked={recordingConsent}
                    onChange={(e) => setRecordingConsent(e.target.checked)}
                    className="rounded border-gray-300 text-[#8437BB] focus:ring-[#8437BB] size-4 flex-shrink-0 mt-0.5"
                  />
                  <span className="text-xs font-bold text-[#616161]">I consent to this interview being recorded and evaluated for this role</span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer font-satoshi bg-[#F7F7F7] p-2.5 rounded">
                  <input
                    type="checkbox"
                    checked={shareWithOtherOpenings}
                    onChange={(e) => setShareWithOtherOpenings(e.target.checked)}
                    className="rounded border-gray-300 text-[#8437BB] focus:ring-[#8437BB] size-4 flex-shrink-0 mt-0.5"
                  />
                  <span className="text-xs font-bold text-[#616161]">Share my interview with other relevant openings on HireVox so I get considered for more roles</span>
                </label>
              </div>

              {/* Start Interview Button */}
              <div className="mt-4 flex-shrink-0">
                <Button
                  onClick={onStartInterview}
                  disabled={isStartingInterview || !guidelinesConfirmed || !recordingConsent}
                  className="w-full bg-[#8437BB] hover:bg-[#732fa3] text-white font-medium py-3 px-6 rounded-xl text-base h-auto"
                >
                  {isStartingInterview ? (
                    <>
                      <LoadingSpinner className="mr-3" />
                      Starting Interview...
                    </>
                  ) : (
                    "Start Interview"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
